import {FetchCAPIError} from '@github-ui/copilot-shared-ui/capi/fetch-capi'
import {useCAPIQuery} from '@github-ui/copilot-shared-ui/capi/use-capi-query'
import {useQueryClient} from '@github-ui/react-query'
import {useCallback, useState} from 'react'

import {
  deleteMcpServer,
  fetchMcpServers,
  putMcpServer,
  SettingsConflictError,
  SettingsUnavailableError,
  UnsafeSettingsError,
  UnsupportedSettingsSchemaError,
} from '../data/mcp-settings-api'
import type {McpServerConfig, McpServerMap} from '../types/mcp'
import {startMcpAttempt} from '../utils/mcp-analytics'

export const mcpServersQueryKey = ['copilot-connectors', 'mcp-servers']

const NO_SERVERS: McpServerMap = {}

function isUnavailable(error: unknown): boolean {
  return error instanceof SettingsUnavailableError || (error instanceof FetchCAPIError && error.status === 403)
}

/**
 * The user's own MCP servers, plus the save and delete actions.
 *
 * Kept separate from `useConnectors` because the two are read from different
 * services. Either can fail on its own, and the page renders each outcome
 * beside the other rather than letting one decide whether it draws at all.
 *
 * `enabled` skips the settings read entirely, for callers gating the feature
 * behind a flag that is off for the current user.
 */
export function useMcpServers(enabled = true) {
  const queryClient = useQueryClient()

  const query = useCAPIQuery<McpServerMap>({
    queryKey: mcpServersQueryKey,
    queryFn: ({signal}) => fetchMcpServers(signal),
    refetchOnWindowFocus: true,
    enabled,
  })

  const refresh = useCallback(() => queryClient.invalidateQueries({queryKey: mcpServersQueryKey}), [queryClient])

  const save = useCallback(
    async (name: string, config: McpServerConfig, editingName?: string) => {
      const attempt = startMcpAttempt(
        editingName === undefined ? 'create' : 'edit',
        config.type,
        editingName !== undefined && name !== editingName,
      )
      try {
        const servers = await putMcpServer(name, config, editingName)
        attempt.finish('success')
        queryClient.setQueryData(mcpServersQueryKey, servers)
        await refresh()
      } catch (error) {
        attempt.finish('failure', error)
        throw new Error(messageFor(error, `Could not save ${name}.`), {cause: error})
      }
    },
    [queryClient, refresh],
  )

  const remove = useCallback(
    async (name: string) => {
      const transport = queryClient.getQueryData<McpServerMap>(mcpServersQueryKey)?.[name]?.type
      const attempt = startMcpAttempt('remove', transport)
      try {
        const servers = await deleteMcpServer(name)
        attempt.finish('success')
        queryClient.setQueryData(mcpServersQueryKey, servers)
        await refresh()
      } catch (error) {
        attempt.finish('failure', error)
        throw new Error(messageFor(error, `Could not remove ${name}.`), {cause: error})
      }
    },
    [queryClient, refresh],
  )

  const unavailable = query.isError && isUnavailable(query.error)
  const freshError =
    enabled && query.isError && !unavailable ? messageFor(query.error, 'Could not load MCP servers.') : null
  const freshErrorAt = freshError ? query.errorUpdatedAt : null

  // A query with no cached data resets to `pending` with its error cleared the
  // moment a retry starts, so the caller's retry control would otherwise
  // disappear behind a bare spinner mid-retry. Remember the error that
  // prompted the retry across that reset, so the control -- and focus on it --
  // stays in place until the retry resolves. Adjusted during render (not an
  // effect) per https://react.dev/reference/react/useState#storing-information-from-previous-renders,
  // since recomputing this from `query` alone cannot see the error once
  // TanStack Query itself clears it for the retry.
  const [lastError, setLastError] = useState<{message: string; at: number} | null>(null)
  if (freshError && freshErrorAt !== null && lastError?.at !== freshErrorAt) {
    setLastError({message: freshError, at: freshErrorAt})
  } else if (!freshError && query.isSuccess && lastError !== null) {
    setLastError(null)
  }

  const retryingAfterError = enabled && query.isLoading && lastError !== null
  const loadError = retryingAfterError ? lastError.message : freshError

  return {
    servers: unavailable ? NO_SERVERS : (query.data ?? NO_SERVERS),
    /** False once the read says this user has no access to personal settings, or the caller disabled the read. */
    available: enabled && !unavailable,
    isLoading: enabled && query.isLoading && !retryingAfterError,
    loadError,
    /**
     * Changes on every settled failure, including a retry that fails with the
     * same message, so a caller can re-announce it. `null` once the read
     * succeeds or the error clears.
     */
    loadErrorAt: retryingAfterError ? lastError.at : freshErrorAt,
    save,
    remove,
    refresh,
  }
}

/** These carry copy already written for users. Anything else gets the fallback. */
function messageFor(error: unknown, fallback: string): string {
  if (
    error instanceof FetchCAPIError ||
    error instanceof SettingsConflictError ||
    error instanceof SettingsUnavailableError ||
    error instanceof UnsafeSettingsError ||
    error instanceof UnsupportedSettingsSchemaError
  ) {
    return error.message
  }
  return fallback
}
